import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Notification, STATUS_TYPES, VALIDATORS, generateResolver, yup } from 'dyl-components';

import onboardingActions from 'actions/onboarding';
import companyActions from 'actions/company';
import { useConfirm } from 'shared/confirmation/useConfirm';
import BusinessProfileForm from './BusinessProfileForm';
import SetupRecommendations from './SetupRecommendations';
import ConfirmSetup from './ConfirmSetup';
import { BUSINESS_PROFILE_SCREENS, INDUSTRY_OPTIONS, OTHER_GOAL, RECOMMENDATION_INPUT_FIELDS, getIndustryCategory, getOptionText } from '../constants';

export const DEFAULT_PROFILE = {
    business_name: '',
    website: '',
    industry_id: '',
    primary_goal: '',
    goal_details: '',
    description: ''
};

// Business name and industry belong to the company profile (GET /v1/company), so they're shown but not editable here.
// Website is only a starting value the customer can change.
const COMPANY_LOCKED_FIELDS = ['business_name', 'industry_id'];
const COMPANY_PREFILL_FIELDS = ['website'];

const BUSINESS_NAME_SCHEMA = VALIDATORS.BUSINESS_NAME().no_whitespace_only();

// Recommendations that need a feature the plan may not include
const RECOMMENDATION_PLAN_FEATURES = {
    business_call_routing: 'has_phone'
};

// The company endpoint uses 0 for "no industry"
const isEmptyValue = (value) => value === '' || value === null || value === undefined || value === 0;

// Adds what later steps need beyond the form: a broad industry category derived from the industry name,
// and the team size, which is the number of seats on the customer's plan (shown to them in Team Setup)
const buildProfile = (profileValues, industryOptions, seats, websiteInsights) => {
    const industry_name = getOptionText(industryOptions, profileValues.industry_id);
    return {
        ...profileValues,
        industry_name,
        industry: industry_name ? getIndustryCategory(industry_name) : '',
        team_size: seats || '',
        website_insights: websiteInsights || null
    };
}

// Fields buildProfile adds; they aren't form values
const DERIVED_PROFILE_FIELDS = ['industry_name', 'industry', 'team_size', 'website_insights'];
const getFormValues = (profile = {}) => Object.fromEntries(Object.entries(profile).filter(([field]) => !DERIVED_PROFILE_FIELDS.includes(field)));

const getDomain = (website = '') => website.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split(/[/?#]/)[0];

const pickRecommendationInputs = (profile) => RECOMMENDATION_INPUT_FIELDS.reduce((inputs, field) => ({ ...inputs, [field]: profile[field] || '' }), {});

const BusinessProfileStep = ({ draft, onDraftChange, onComplete }) => {
    const dispatch = useDispatch();
    const { isConfirmed } = useConfirm();

    const { user_id, isDiscoveringWebsite, isGeneratingRecommendations, company, industries, isReadingCompany, seats, plan } = useSelector(state => ({
        user_id: state.auth.user_id,
        isDiscoveringWebsite: state.onboarding.isDiscoveringWebsite,
        isGeneratingRecommendations: state.onboarding.isGeneratingRecommendations,
        company: state.company.company,
        industries: state.company.industry,
        isReadingCompany: state.company.isReadingCompany || state.company.isReadingCompanyIndustry,
        seats: state.onboarding.seats?.total_seats,
        plan: state.onboarding.plan
    }));

    // DYL's own industry list, falling back to a short built-in list if it can't be loaded
    const industryOptions = useMemo(() => (
        industries?.length ? industries.map(({ id, name }) => ({ key: id, value: id, text: name })) : INDUSTRY_OPTIONS
    ), [industries]);

    // Returning to an approved step opens its review screen
    const [screen, setScreen] = useState(draft?.approval ? BUSINESS_PROFILE_SCREENS.CONFIRM : (draft?.screen || BUSINESS_PROFILE_SCREENS.PROFILE));
    const [recommendations, setRecommendations] = useState(draft?.recommendations || []);
    const [selected, setSelected] = useState(draft?.selected || []);
    const [generatedFor, setGeneratedFor] = useState(draft?.generated_for || null);
    const [approval, setApproval] = useState(draft?.approval || null);
    const [websiteFindings, setWebsiteFindings] = useState(null);
    // What website analysis learned, once the customer chose to use it
    const [websiteInsights, setWebsiteInsights] = useState(draft?.profile?.website_insights || null);

    const lockedFieldsRef = useRef([]);
    const { control, handleSubmit, getValues, setValue, watch, trigger, formState: { isValid } } = useForm({
        mode: 'onChange',
        defaultValues: { ...DEFAULT_PROFILE, ...getFormValues(draft?.profile) },
        resolver: generateResolver({
            // The company's own name is accepted as is; the business name rules only apply when it has to be typed in
            business_name: yup.string().required('This field is required').test('business_name', function (value) {
                if (lockedFieldsRef.current.includes('business_name')) {
                    return true;
                }
                try {
                    BUSINESS_NAME_SCHEMA.validateSync(value);
                    return true;
                } catch (e) {
                    return this.createError({ message: e.message });
                }
            }),
            website: VALIDATORS.WEBSITE(),
            industry_id: yup.mixed().test('required', 'This field is required', value => !isEmptyValue(value)),
            primary_goal: yup.string().required('This field is required'),
            goal_details: yup.string().maxlength(500).when('primary_goal', {
                is: OTHER_GOAL,
                then: schema => schema.trim().required('Describe your goal so we can tailor your setup')
            }),
            description: yup.string().maxlength(1000)
        })
    });

    const values = watch();
    const profileJSON = JSON.stringify(values);

    const profile = buildProfile(values, industryOptions, seats, websiteInsights);

    // Insights describe one website; a different website makes them stale
    const websiteDomain = getDomain(values.website);
    useEffect(() => {
        if (websiteInsights && websiteInsights.website !== websiteDomain) {
            setWebsiteInsights(null);
        }
    }, [websiteInsights, websiteDomain]);

    const [isCompanyDataReady, setIsCompanyDataReady] = useState(false);
    useEffect(() => {
        Promise.allSettled([
            dispatch(companyActions.readCompany()),
            dispatch(companyActions.readIndustry())
        ]).then(() => setIsCompanyDataReady(true));
    }, [dispatch]);

    // Locked fields always take the company's current values; a field stays editable only if the company has no value for it
    const hasPrefilled = useRef(false);
    const [prefilledFields, setPrefilledFields] = useState([]);
    const [lockedFields, setLockedFields] = useState([]);
    useEffect(() => {
        if (hasPrefilled.current || !isCompanyDataReady || !company) {
            return;
        }
        hasPrefilled.current = true;
        const industryId = industryOptions.some(({ value }) => value === company.industry?.id) ? company.industry.id : '';
        const companyValues = { business_name: company.name, website: company.website, industry_id: industryId };

        const locked = COMPANY_LOCKED_FIELDS.filter(field => !isEmptyValue(companyValues[field]));
        lockedFieldsRef.current = locked;
        locked.forEach(field => {
            if (getValues(field) !== companyValues[field]) {
                setValue(field, companyValues[field], { shouldValidate: true });
            }
        });
        setLockedFields(locked);

        // An approved draft keeps its website; otherwise fill it only when empty
        const filled = draft?.approval ? [] : COMPANY_PREFILL_FIELDS.filter(field => isEmptyValue(getValues(field)) && !isEmptyValue(companyValues[field]));
        filled.forEach(field => setValue(field, companyValues[field], { shouldValidate: true }));
        setPrefilledFields(filled);
        trigger('business_name');
    }, [isCompanyDataReady, company, industryOptions, draft, getValues, setValue, trigger]);

    // Whether goal details are required depends on the goal, so re-check them when the goal changes
    const primaryGoal = values.primary_goal;
    const previousPrimaryGoal = useRef(primaryGoal);
    useEffect(() => {
        if (previousPrimaryGoal.current !== primaryGoal) {
            previousPrimaryGoal.current = primaryGoal;
            trigger('goal_details');
        }
    }, [primaryGoal, trigger]);

    // An approval only covers what the user saw; any later edit requires approving again
    const approvalSnapshot = JSON.stringify({ profile: JSON.parse(profileJSON), websiteInsights, selected: [...selected].sort() });
    useEffect(() => {
        if (approval && approval.snapshot !== approvalSnapshot) {
            setApproval(null);
        }
    }, [approval, approvalSnapshot]);

    const getStepDraft = (overrides = {}) => ({
        screen,
        profile: buildProfile(getValues(), industryOptions, seats, websiteInsights),
        recommendations,
        selected,
        generated_for: generatedFor,
        approval,
        ...overrides
    });

    // Every change is reported to the wizard so leaving or going back never loses progress
    useEffect(() => {
        onDraftChange({
            screen,
            profile: buildProfile(JSON.parse(profileJSON), industryOptions, seats, websiteInsights),
            recommendations,
            selected,
            generated_for: generatedFor,
            approval
        });
    }, [onDraftChange, profileJSON, industryOptions, seats, websiteInsights, screen, recommendations, selected, generatedFor, approval]);

    const onDiscoverWebsite = async () => {
        const isWebsiteValid = await trigger('website');
        if (!isWebsiteValid) {
            return;
        }
        try {
            const findings = await dispatch(onboardingActions.discoverWebsite({ website: getValues('website'), industry: profile.industry }));
            // Website analysis returns an industry category; offer the first DYL industry in that category
            const suggestions = findings.suggestions.map(suggestion => {
                if (suggestion.field !== 'industry') {
                    return suggestion;
                }
                const option = industryOptions.find(({ text }) => getIndustryCategory(text) === suggestion.value);
                return option ? { ...suggestion, field: 'industry_id', value: option.value, display: option.text } : null;
            }).filter(suggestion => suggestion && !lockedFields.includes(suggestion.field) && isEmptyValue(getValues(suggestion.field)));
            // Only offer to fill fields that are empty and not locked to the company profile
            setWebsiteFindings({ ...findings, suggestions });
        } catch (e) {
            Notification.alert('Failed to analyze website', STATUS_TYPES.ERROR);
        }
    }

    const onApplyFindings = () => {
        // Only fill empty fields so manual entries are never overwritten
        const applied = websiteFindings.suggestions.filter(({ field }) => isEmptyValue(getValues(field)));
        applied.forEach(({ field, value }) => {
            setValue(field, value, { shouldValidate: true, shouldDirty: true });
        });
        setWebsiteInsights(websiteFindings.insights);
        Notification.alert(`We'll use what we learned from ${websiteFindings.website} for your recommendations`, STATUS_TYPES.SUCCESS);
        setWebsiteFindings(null);
    }

    const onGenerate = async (formValues) => {
        const fullProfile = buildProfile(formValues, industryOptions, seats, websiteInsights);
        const inputs = pickRecommendationInputs(fullProfile);
        const areRecommendationsCurrent = generatedFor && JSON.stringify(inputs) === JSON.stringify(pickRecommendationInputs(generatedFor));
        if (areRecommendationsCurrent && recommendations.length) {
            setScreen(BUSINESS_PROFILE_SCREENS.RECOMMENDATIONS);
            return;
        }

        const hasIndustryChanged = generatedFor && generatedFor.industry_id !== formValues.industry_id;
        if (hasIndustryChanged) {
            const shouldRegenerate = await isConfirmed(
                'Regenerate recommendations?',
                'You changed your industry. New recommendations will replace your current selections.'
            );
            if (!shouldRegenerate) {
                return;
            }
        }

        try {
            const { recommendations: newRecommendations } = await dispatch(onboardingActions.generateRecommendations(fullProfile));
            const previousIds = hasIndustryChanged ? [] : recommendations.map(({ id }) => id);
            // Keep the user's choices for recommendations they've already seen
            setSelected(newRecommendations.filter(({ id, default_selected }) => (
                previousIds.includes(id) ? selected.includes(id) : default_selected
            )).map(({ id }) => id));
            setRecommendations(newRecommendations);
            setGeneratedFor(inputs);
            setApproval(null);
            setScreen(BUSINESS_PROFILE_SCREENS.RECOMMENDATIONS);
        } catch (e) {
            Notification.alert('Failed to generate recommendations', STATUS_TYPES.ERROR);
        }
    }

    const onToggleRecommendation = (id) => {
        setSelected(selected.includes(id) ? selected.filter(selectedId => selectedId !== id) : [...selected, id]);
    }

    const onConfirm = () => {
        // TODO: send the approved configuration draft to the backend once the endpoint exists
        const newApproval = { approved_by: user_id, approved_at: new Date().toISOString(), snapshot: approvalSnapshot };
        setApproval(newApproval);
        onComplete(getStepDraft({ approval: newApproval }));
        Notification.alert('Business profile saved as a draft', STATUS_TYPES.SUCCESS);
    }

    // Hide what the plan can't use; choices stay in the draft in case the plan changes
    const isIncludedInPlan = ({ id }) => !plan || plan[RECOMMENDATION_PLAN_FEATURES[id]] !== false;
    const availableRecommendations = recommendations.filter(isIncludedInPlan);
    const availableSelected = selected.filter(id => availableRecommendations.some(recommendation => recommendation.id === id));

    switch (screen) {
        case BUSINESS_PROFILE_SCREENS.RECOMMENDATIONS:
            return (
                <SetupRecommendations
                    profile={profile}
                    recommendations={availableRecommendations}
                    selected={availableSelected}
                    onToggle={onToggleRecommendation}
                    onBack={() => setScreen(BUSINESS_PROFILE_SCREENS.PROFILE)}
                    onContinue={() => setScreen(BUSINESS_PROFILE_SCREENS.CONFIRM)}
                />
            );
        case BUSINESS_PROFILE_SCREENS.CONFIRM:
            return (
                <ConfirmSetup
                    business_name={profile.business_name}
                    recommendations={availableRecommendations.filter(({ id }) => selected.includes(id))}
                    isApprovedInitially={!!approval}
                    onBack={() => setScreen(BUSINESS_PROFILE_SCREENS.RECOMMENDATIONS)}
                    onConfirm={onConfirm}
                />
            );
        default:
            return (
                <BusinessProfileForm
                    control={control}
                    industryOptions={industryOptions}
                    primaryGoal={values.primary_goal}
                    prefilledFields={prefilledFields}
                    lockedFields={lockedFields}
                    websiteInsights={websiteInsights}
                    onRemoveInsights={() => setWebsiteInsights(null)}
                    isReadingCompany={isReadingCompany}
                    isValid={isValid}
                    onDiscoverWebsite={onDiscoverWebsite}
                    isDiscoveringWebsite={isDiscoveringWebsite}
                    websiteFindings={websiteFindings}
                    onApplyFindings={onApplyFindings}
                    onDismissFindings={() => setWebsiteFindings(null)}
                    onGenerate={handleSubmit(onGenerate)}
                    isGeneratingRecommendations={isGeneratingRecommendations}
                />
            );
    }
}

export default BusinessProfileStep;
